import React from "react";
import globals from '../Globals'
import requests from "../Auth/Requests";
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { Box, Typography, Paper, List, ListItem, ListItemText, Button, IconButton, Divider, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, DialogContentText } from '@mui/material';
import { ExpandMore, ExpandLess, Remove, Edit, Check } from '@mui/icons-material';

const ROLE_LABELS = {
    users: "Admins",
    database: "Database",
    servers: "Game Servers",
    players: "Players"
};

export default class AdminsPage extends React.Component {
    state = {
        userData: null,
        showDeleted: false,
        foldedUsers: {},
        renamingDevice: null,
        renamingValue: "",
        addUserDialogOpen: false,
        newUserName: "",
        newUserEmail: "",
        changeRolesDialogOpen: false,
        rolesEditUser: null,
        roleSelections: {},
        locationDialogOpen: false,
        locationDialogContent: ""
    }
    openLocationDialog = (location) => {
        const { query, status, lat, lon, ...rest } = location;
        const cleaned = {
            ip: query,
            ...rest
        };

        this.setState({
            locationDialogOpen: true,
            locationDialogContent: {
                ...cleaned,
                map: { lat, lon }
            }
        });
    }

    closeLocationDialog = () => {
        this.setState({ locationDialogOpen: false });
    }

    toggleUserFold = (userId) => {
        this.setState(prev => ({
            foldedUsers: {
                ...prev.foldedUsers,
                [userId]: !prev.foldedUsers[userId]
            }
        }));
    }

    performAction(description, ...args) {
        const cmd = args.join(" ");

        const key = enqueueSnackbar(`${description}...`, {
            variant: 'info',
            persist: true
        });

        requests.request("users " + cmd, {})
            .then(response => {
                this.setState({ userData: response });
                closeSnackbar(key);
                enqueueSnackbar(`${description} successful!`, {
                    variant: 'success'
                });
            })
            .catch(error => {
                closeSnackbar(key);
                enqueueSnackbar(`${description} failed: ${error.message}`, {
                    variant: 'error'
                });
            });
    }

    renderUsers = () => {
        const { users } = this.state.userData;
        const allowedUsers = users.filter(u => u.allowed);
        const disallowedUsers = users.filter(u => !u.allowed);

        users.forEach(user => {
            const hasDevices = user.devices.length > 0;
            const hasPendingAuthorization = user.devices.some(device => !device.authorized);
            const shouldStartFolded = !hasDevices || !hasPendingAuthorization;
            if (this.state.foldedUsers[user.id] === undefined) {
                this.state.foldedUsers[user.id] = shouldStartFolded;
            }
        });

        const pendingAuthUsers = users.filter(user =>
            user.devices.some(device => !device.authorized)
        );

        const renderUserCard = (user, isDisallowed = false) => {
            const hasDevices = user.devices.length > 0;

            return (
                <Paper
                    key={user.id}
                    elevation={3}
                    sx={{
                        marginBottom: 3,
                        padding: 2,
                        backgroundColor: isDisallowed ? '#f0f0f0' : 'white'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: this.state.foldedUsers[user.id] ? 0 : 1
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', opacity: isDisallowed ? 0.6 : 1 }}>
                            <Typography variant="h6">
                                {user.name} ({user.email})
                            </Typography>
                            {hasDevices ? (
                                <IconButton size="small" onClick={() => this.toggleUserFold(user.id)}>
                                    {this.state.foldedUsers[user.id] ? <ExpandMore /> : <ExpandLess />}
                                </IconButton>
                            ) : (
                                <Remove sx={{ marginLeft: 1 }} />
                            )}
                        </Box>
                        <Box>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="medium"
                                onClick={() => this.handleChangeRoles(user.id)}
                                sx={{ minHeight: '40px', marginRight: 1 }}
                            >
                                Change Roles
                            </Button>
                            <Button
                                variant="outlined"
                                color={isDisallowed ? 'success' : 'error'}
                                size="medium"
                                onClick={() => isDisallowed ? this.handleRestoreUser(user.id) : this.handleDeleteUser(user.id)}
                                sx={{ minHeight: '40px' }}
                            >
                                {isDisallowed ? 'Restore' : 'Delete'}
                            </Button>
                        </Box>
                    </Box>
                    {hasDevices && !this.state.foldedUsers[user.id] && (
                        <Box sx={{ opacity: isDisallowed ? 0.6 : 1, marginTop: 1 }}>
                            <List>
                                {user.devices.map((device, index) => (
                                    <ListItem
                                        key={device.id}
                                        divider={index !== user.devices.length - 1}
                                        secondaryAction={
                                            <Box>
                                                {!device.authorized && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="medium"
                                                        onClick={() => this.handleAuthorize(user.id, device.id)}
                                                        sx={{ minHeight: '40px', marginRight: 1 }}
                                                    >
                                                        Authorize
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="medium"
                                                    onClick={() => this.handleDeleteDevice(user.id, device.id)}
                                                    sx={{ minHeight: '40px' }}
                                                >
                                                    Delete
                                                </Button>
                                            </Box>
                                        }
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    Device:&nbsp;
                                                    {this.state.renamingDevice?.userId === user.id && this.state.renamingDevice?.deviceId === device.id ? (
                                                        <>
                                                            <TextField
                                                                size="small"
                                                                variant="standard"
                                                                value={this.state.renamingValue}
                                                                onChange={this.handleRenameChange}
                                                                sx={{ maxWidth: 200 }}
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                onClick={this.handleRenameSubmit}
                                                                sx={{ marginLeft: 1 }}
                                                            >
                                                                <Check fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {device.name ?? device.id}
                                                            <IconButton
                                                                size="small"
                                                                sx={{ marginLeft: 1 }}
                                                                onClick={() => this.handleRenameDevice(user.id, device.id, device.name)}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <>
                                                    Logged In: {device.loggedIn ? 'Yes' : 'No'} | Last Interaction: {device.lastInteraction}
                                                    {device.location && (
                                                        <>
                                                            {' | '}
                                                            <Typography
                                                                variant="body2"
                                                                component="span"
                                                                onClick={() => this.openLocationDialog(device.location)}
                                                                sx={{ cursor: 'pointer', textDecoration: 'underline', display: 'inline', marginLeft: '4px' }}
                                                            >
                                                                {device.location.country}, {device.location.regionName}, {device.location.city}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </Paper>
            );
        };

        return (
            <Box sx={{ padding: 2 }}>
                {pendingAuthUsers.length > 0 && (
                    <Typography sx={{ backgroundColor: '#fff3cd', color: '#856404', padding: 2, borderRadius: 1, marginBottom: 2 }}>
                        Pending Authorization: {pendingAuthUsers.map(u => `${u.name} (${u.email})`).join(', ')}
                    </Typography>
                )}
                {allowedUsers.map(user => renderUserCard(user))}
                <Box sx={{ textAlign: 'center', marginY: 2 }}>
                    <Button variant="contained" color="primary" onClick={this.handleAddUser}>
                        Add User
                    </Button>
                </Box>
                {disallowedUsers.length > 0 && (
                    <>
                        <Box sx={{ textAlign: 'left', marginBottom: 2 }}>
                            <Button
                                variant="text"
                                onClick={() => this.setState(prev => ({ showDeleted: !prev.showDeleted }))}
                            >
                                {this.state.showDeleted ? 'Hide Deleted Users' : 'Show Deleted Users'}
                            </Button>
                        </Box>
                        {this.state.showDeleted && (
                            <>
                                {disallowedUsers.map(user => renderUserCard(user, true))}
                            </>
                        )}
                    </>
                )}
                <Dialog open={this.state.addUserDialogOpen} onClose={this.handleCancelAddUser}>
                    <DialogTitle>Add New User</DialogTitle>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            label="Name"
                            fullWidth
                            value={this.state.newUserName}
                            onChange={(e) => this.setState({ newUserName: e.target.value })}
                        />
                        <TextField
                            margin="dense"
                            label="Email"
                            fullWidth
                            value={this.state.newUserEmail}
                            onChange={(e) => this.setState({ newUserEmail: e.target.value })}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleCancelAddUser}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleConfirmAddUser}>Add</Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={this.state.changeRolesDialogOpen}
                    onClose={this.handleCancelRoles}
                    maxWidth="sm"
                    fullWidth
                >
                    {/* No title */}
                    <DialogContent>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Role</b></TableCell>
                                    <TableCell align="center" sx={{ width: '80px' }}><b>View</b></TableCell>
                                    <TableCell align="center" sx={{ width: '80px' }}><b>Edit</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {this.state.userData.knownRoles.map((role, index) => (
                                    <TableRow
                                        key={role}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                                            '& td': { paddingTop: '4px', paddingBottom: '4px' }
                                        }}
                                    >
                                        <TableCell>{ROLE_LABELS[role] ?? role}</TableCell>
                                        <TableCell align="center" sx={{ width: '80px' }}>
                                            <Checkbox
                                                checked={this.state.roleSelections?.[role]?.view || false}
                                                onChange={() => this.handleRoleToggle(role, "view")}
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '80px' }}>
                                            <Checkbox
                                                checked={this.state.roleSelections?.[role]?.edit || false}
                                                onChange={() => this.handleRoleToggle(role, "edit")}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleCancelRoles}>Cancel</Button>
                        <Button variant="contained" onClick={this.handleConfirmRoles}>Save</Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={this.state.locationDialogOpen} onClose={this.closeLocationDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Location Details</DialogTitle>
                    <DialogContent>
                        <DialogContentText component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {this.state.locationDialogContent && (() => {
                                const l = this.state.locationDialogContent;
                                return [
                                    `IP: ${l.ip}`,
                                    `Country: ${l.country} (${l.countryCode})`,
                                    `Region: ${l.regionName} (${l.region})`,
                                    `City: ${l.city}`,
                                    `ZIP: ${l.zip}`,
                                    `Time Zone: ${l.timezone}`,
                                    `ISP: ${l.isp}`,
                                    `ORG: ${l.org}`
                                ].join('\n');
                            })()}
                        </DialogContentText>
                        {this.state.locationDialogContent && this.state.locationDialogContent.map && (
                            <Box sx={{ marginTop: 2 }}>
                                <iframe
                                    title="location-map"
                                    width="100%"
                                    height="250"
                                    frameBorder="0"
                                    style={{ border: 0 }}
                                    src={`https://www.google.com/maps?q=${this.state.locationDialogContent.map.lat},${this.state.locationDialogContent.map.lon}&z=14&output=embed`}
                                    allowFullScreen
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.closeLocationDialog}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    handleAuthorize = (userId, deviceId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Authorize device for ${user.name}`, "authorize-device", deviceId);
    }

    handleDeleteDevice = (userId, deviceId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Delete device for ${user.name}`, "delete-device", deviceId);
    }

    handleDeleteUser = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Delete user ${user.name}`, "delete-user", userId);
    }

    handleChangeRoles = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        const selections = {};
        const userRoles = Array.isArray(user.roles) ? user.roles : [];

        this.state.userData.knownRoles.forEach(role => {
            selections[role] = {
                view: userRoles.includes(role) || userRoles.includes(`#${role}`),
                edit: userRoles.includes(role)
            };
        });

        this.setState({ changeRolesDialogOpen: true, rolesEditUser: user, roleSelections: selections });
    }

    handleRestoreUser = (userId) => {
        const user = this.state.userData.users.find(u => u.id === userId);
        this.performAction(`Restore user ${user.name}`, "restore-user", userId);
    }

    handleAddUser = () => {
        this.setState({ addUserDialogOpen: true });
    }

    handleConfirmAddUser = () => {
        const { newUserName, newUserEmail } = this.state;
        this.performAction(`Add user ${newUserName}`, "add-user", newUserName, newUserEmail);
        this.setState({ addUserDialogOpen: false, newUserName: "", newUserEmail: "" });
    }

    handleCancelAddUser = () => {
        this.setState({ addUserDialogOpen: false });
    }

    handleRenameDevice = (userId, deviceId, currentName) => {
        this.setState({
            renamingDevice: { userId, deviceId },
            renamingValue: currentName || ""
        });
    }

    handleRenameChange = (e) => {
        this.setState({ renamingValue: e.target.value });
    }

    handleRenameSubmit = () => {
        const { userId, deviceId } = this.state.renamingDevice;
        const newName = this.state.renamingValue.trim().replace(/\s+/g, "-");
        if (newName) {
            const user = this.state.userData.users.find(u => u.id === userId);
            this.performAction(`Rename device for ${user.name}`, "rename-device", deviceId, newName);
        }
        this.setState({ renamingDevice: null, renamingValue: "" });
    }

    handleRoleToggle = (role, type) => {
        this.setState(prev => {
            const selections = { ...prev.roleSelections };
            const current = { ...selections[role] };

            if (type === 'edit') {
                current.edit = !current.edit;
                if (current.edit) current.view = true;
            }

            if (type === 'view') {
                current.view = !current.view;
                if (!current.view) current.edit = false;
            }

            selections[role] = current;
            return { roleSelections: selections };
        });
    }

    handleConfirmRoles = () => {
        const { rolesEditUser, roleSelections } = this.state;
        const selectedRoles = Object.entries(roleSelections).flatMap(([role, perms]) => {
            if (perms.view && perms.edit) return [role];
            if (perms.view) return [`#${role}`];
            return [];
        });
        this.performAction(`Update roles for ${rolesEditUser.name}`, "set-roles", rolesEditUser.id, ...selectedRoles);
        this.setState({ changeRolesDialogOpen: false, rolesEditUser: null, roleSelections: {} });
    }

    handleCancelRoles = () => {
        this.setState({ changeRolesDialogOpen: false });
    }

    render() {
        if (this.state.userData == null) {
            return <div>Loading...</div>;
        } else {
            return this.renderUsers();
        }
    }

    componentDidMount() {
        this.performAction("Loading", "list");
    }
}